<script setup lang="ts">
import { BreadcrumbDataQuery, BreadcrumbDataQueryVariables } from '@/generated/graphql'
import { useRoute, useRouter } from 'vue-router'
import { useApolloClient, useQuery } from '@vue/apollo-composable'
import gql from 'graphql-tag'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import BreadcrumbTrail, { BreadcrumbItem } from '@/components/BreadcrumbTrail.vue'
import { itemSortByName } from '@/app'
import { cachedBopVersion, cachedEntityName } from '@/utils/breadcrumbCache'

const { t } = useI18n()
const route = useRoute()
const router = useRouter()
const { client } = useApolloClient()

const inLibrary = computed(() => route.meta.library === true)

const productId = computed(() => route.params.productId as string | undefined)
const configId = computed(() => route.params.productConfigId as string | undefined)
const siteId = computed(() => route.params.siteId as string | undefined)
const bopId = computed(() => route.params.bopId as string | undefined)
const moduleId = computed(() => route.params.moduleId as string | undefined)
const ebomId = computed(() => route.params.ebomId as string | undefined)
const stepId = computed(() => route.params.stepId as string | undefined)

const fetchQuery = useQuery<BreadcrumbDataQuery, BreadcrumbDataQueryVariables>(
  gql`
    query BreadcrumbData(
      $productId: ID!
      $includeProduct: Boolean!
      $configId: ID!
      $includeConfig: Boolean!
      $siteId: ID!
      $includeSite: Boolean!
      $bopId: ID!
      $includeBop: Boolean!
      $includeModules: Boolean!
      $includeEboms: Boolean!
      $stepId: ID!
      $includeStep: Boolean!
    ) {
      product {
        product(id: $productId) @include(if: $includeProduct) {
          id
          name
        }
        productConfiguration(id: $configId) @include(if: $includeConfig) {
          id
          name
        }
        bop(id: $bopId) @include(if: $includeBop) {
          id
          version
          versionStatus
          nodes @include(if: $includeModules) {
            module {
              id
              name
            }
          }
        }
        step(id: $stepId) @include(if: $includeStep) {
          id
          name
        }
      }
      site {
        site(id: $siteId) @include(if: $includeSite) {
          id
          name
        }
      }
      article {
        ebomsForBop(bopId: $bopId) @include(if: $includeEboms) {
          id
          name
        }
      }
    }
  `,
  () => ({
    productId: productId.value || '',
    includeProduct: !!productId.value,
    configId: configId.value || '',
    includeConfig: !!configId.value,
    siteId: siteId.value || '',
    includeSite: !!siteId.value,
    bopId: bopId.value || '',
    includeBop: !!bopId.value,
    includeModules: route.name === 'module',
    includeEboms: route.name === 'ebom',
    stepId: stepId.value || '',
    includeStep: inLibrary.value && !!stepId.value,
  }),
  () => ({
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
    returnPartialData: true,
    keepPreviousResult: true,
    enabled: !!productId.value || (inLibrary.value && !!stepId.value),
  }),
)

const bop = computed(() => fetchQuery.result.value?.product?.bop)
const libraryStep = computed(() => fetchQuery.result.value?.product?.step)

const productName = computed(
  () =>
    fetchQuery.result.value?.product?.product?.name ??
    cachedEntityName(client, 'Product', productId.value),
)
const configName = computed(
  () =>
    fetchQuery.result.value?.product?.productConfiguration?.name ??
    cachedEntityName(client, 'ProductConfiguration', configId.value),
)
const siteName = computed(
  () => fetchQuery.result.value?.site?.site?.name ?? cachedEntityName(client, 'Site', siteId.value),
)
const bopVersion = computed(() =>
  bop.value?.version !== undefined && bop.value.versionStatus
    ? { version: bop.value.version, versionStatus: bop.value.versionStatus }
    : cachedBopVersion(client, bopId.value),
)

const configSelection = computed(() => {
  if (!configName.value) {
    return undefined
  }
  return siteName.value ? `${configName.value} - ${siteName.value}` : configName.value
})
const bopSelection = computed(() =>
  bopVersion.value
    ? `${bopVersion.value.version} ${t(`component.versionStatus.status.${bopVersion.value.versionStatus}`)}`
    : undefined,
)

const moduleOptions = computed(() =>
  itemSortByName(
    bop.value?.nodes
      ?.filter((n) => n.module?.name)
      .map((n) => ({ title: n.module.name, value: n.module.id })) || [],
    (i) => i.title,
  ),
)
const currentModuleName = computed(
  () =>
    bop.value?.nodes?.find((n) => n.module?.id === moduleId.value)?.module.name ??
    cachedEntityName(client, 'Module', moduleId.value),
)

const ebomOptions = computed(() =>
  itemSortByName(
    fetchQuery.result.value?.article?.ebomsForBop
      ?.filter((e) => e.name)
      .map((e) => ({ title: e.name, value: e.id })) || [],
    (i) => i.title,
  ),
)
const currentEbomName = computed(
  () =>
    fetchQuery.result.value?.article?.ebomsForBop?.find((e) => e.id === ebomId.value)?.name ??
    cachedEntityName(client, 'Ebom', ebomId.value),
)

const items = computed<BreadcrumbItem[]>(() => {
  if (inLibrary.value) {
    if (!stepId.value) {
      return [{ pageLabel: t('view.organization.library.title') }]
    }
    return [
      { pageLabel: t('view.organization.library.title'), to: { name: 'library' } },
      { pageLabel: libraryStep.value?.name || '' },
    ]
  }

  const trail: BreadcrumbItem[] = [
    {
      pageLabel: t('entity.product.plural'),
      to: { name: 'products' },
      selection: productName.value,
    },
  ]

  if (route.name === 'productConfigurations') {
    trail.push({ pageLabel: t('entity.productConfiguration.singular') })
    return trail
  }
  trail.push({
    pageLabel: t('entity.productConfiguration.singular'),
    to: { name: 'productConfigurations' },
    selection: configSelection.value,
  })

  if (route.name === 'bops') {
    trail.push({ pageLabel: t('entity.bop.plural') })
    return trail
  }
  trail.push({
    pageLabel: t('entity.bop.plural'),
    to: { name: 'bops' },
    selection: bopSelection.value,
  })

  if (route.name === 'bopProcess') {
    trail.push({ pageLabel: t('entity.bop.singular') })
    return trail
  }
  trail.push({ pageLabel: t('entity.bop.singular'), to: { name: 'bopProcess' } })

  if (route.name === 'module') {
    trail.push({
      pageLabel: currentModuleName.value || t('entity.module.singular'),
      dropdown: {
        options: moduleOptions.value,
        activeId: moduleId.value,
        loading: fetchQuery.loading.value,
        onSelect: (id) =>
          router.push({ name: 'module', params: { moduleId: id, nodeId: undefined } }),
      },
    })
  } else if (route.name === 'ebom') {
    trail.push({
      pageLabel: currentEbomName.value || t('entity.ebom.singular'),
      dropdown: {
        options: ebomOptions.value,
        activeId: ebomId.value,
        loading: fetchQuery.loading.value,
        onSelect: (id) =>
          router.push({ name: 'ebom', params: { ebomId: id, ebomPartId: undefined } }),
      },
    })
  }
  return trail
})
</script>

<template>
  <breadcrumb-trail :items="items" />
</template>
